import { c as _c } from "react/compiler-runtime";
import { createContext, type Dispatch, type PropsWithChildren, useContext, useState, useSyncExternalStore } from 'react';
import { createReducerStore } from '../helpers/createReducerStore';
import { type ConversationAction, type ConversationsState, initialConversationsState } from './context';
import { type ConversationActions, type ConversationsStore, createConversationActions } from './conversationActions';
import { conversationReducer } from './reducers/conversationsReducer';

// The conversations live in a store, not in context state: the context value
// (the store and its actions) never changes, and each component subscribes to
// the slice it renders through useConversationsSelector. Typing in one chat
// used to rerender every other chat window, the header and every avatar.

type ConversationsContextValue = {
  store: ConversationsStore;
  actions: ConversationActions;
};
const createConversationsContextValue = (store: ConversationsStore): ConversationsContextValue => ({
  store,
  actions: createConversationActions(store)
});

// Outside a provider (some tests) nothing changes: the store keeps its
// initial state, as the old context's no-op dispatch did.
const ConversationsContext = createContext<ConversationsContextValue>(createConversationsContextValue(createReducerStore((state: ConversationsState, _action: ConversationAction) => state, initialConversationsState)));

/** The store and its actions. They never change, so this never rerenders. */
export const useConversationsStore = () => {
  return useContext(ConversationsContext);
};

/**
 * One slice of the conversations state. The caller rerenders only when the
 * selected value changes (Object.is), so a selector must return a primitive or
 * a slice the reducer keeps when it is unchanged — never a new array or
 * object. Derive arrays in the component from a selected slice.
 */
export const useConversationsSelector = selector => {
  const $ = _c(5);
  const {
    store
  } = useContext(ConversationsContext);
  let t0;
  if ($[0] !== selector || $[1] !== store) {
    t0 = () => selector(store.getState());
    $[0] = selector;
    $[1] = store;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== selector) {
    t1 = () => selector(initialConversationsState);
    $[3] = selector;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useSyncExternalStore(store.subscribe, t0, t1);
};

/**
 * The whole state and a dispatch, as the context used to provide them.
 * Rerenders on every change; prefer useConversationsSelector.
 */
export const useConversationsState = () => {
  const $ = _c(3);
  const {
    store
  } = useContext(ConversationsContext);
  const state = useConversationsSelector(_temp);
  let t0;
  if ($[0] !== state || $[1] !== store.dispatch) {
    t0 = {
      state,
      dispatch: store.dispatch
    };
    $[0] = state;
    $[1] = store.dispatch;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const ConversationsContextProvider = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  const [value] = useState(_temp2);
  let t1;
  if ($[0] !== children || $[1] !== value) {
    t1 = <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>;
    $[0] = children;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp(s) {
  return s;
}
function _temp2() {
  return createConversationsContextValue(createReducerStore(conversationReducer, initialConversationsState));
}